import React, { FC, useMemo, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Button from "@/components/Button";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { Typography } from "@mui/material";
import addValidationSchema, { AddOUFormData } from "./validation.schema";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { a11yProps } from "@/utils/a11yProps";
import TabPanel from "@/components/TabPanel";
import { TabOrganizationGeneral } from "./TabOrganizationGeneral";
import { TabOrganizationAddress } from "./TabOrganizationAddress";
import { TabOrganizationContact } from "./TabOrganizationContact";
import { TabOrganizationAdditionalInfo } from "./TabOrganizationAdditionalInfo";
import { useUpdateOrganization } from "@/hooks/management/useOrganizations.manage";
import { Organization } from "@/domain/organizations.interface";
import { useCountrySetting } from "@/hooks/useSetting";

type OrganizationUpdateModalProps = {
  open: boolean;
  organization: Organization;
  onClose: () => void;
};

const OrganizationUpdateModal: FC<OrganizationUpdateModalProps> = ({
  open,
  organization,
  onClose,
}) => {
  const { t } = useTranslation("organization");
  const { t: tCommon } = useTranslation("common");
  const { userInfo } = useUserStore();
  const { showSnackBar } = useFeedBack();
  const theme = useAppTheme();

  const countrySetting = useCountrySetting();
  const country = useMemo(() => countrySetting, [countrySetting]);

  const createAuditLog = useCreateAuditlog();
  const updateOrganization = useUpdateOrganization();

  const [selectedTab, setSelectedTab] = useState(0);

  const defaultValues = useMemo(() => {
    return {
      name: organization.name,
      description: organization.description,
      groupLabelIds: organization.groupLabelIds,
      addressLine1: organization.address.addressLine1,
      addressLine2: organization.address.addressLine2,
      city: organization.address.city,
      region: organization.address.region,
      zipCode: organization.address.zipCode,
      contactName: organization.contact.name,
      contactPhone: organization.contact.phoneNumber,
      npi: organization.npi,
      status: organization.status,
    };
  }, [organization]);

  const methods = useForm<AddOUFormData>({
    resolver: yupResolver(addValidationSchema(tCommon)),
    mode: "all",
    defaultValues,
  });

  const handleTabChange = async (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setSelectedTab(newValue);
  };

  const onSubmit = async (data: AddOUFormData) => {
    const payload = {
      name: data.name as string,
      description: data.description as string,
      groupLabelIds: data.groupLabelIds as string[],
      addressLine1: data.addressLine1 as string,
      addressLine2: data.addressLine2 as string,
      city: data.city as string,
      region: data.region as string,
      zipCode: data.zipCode as string,
      contactName: data.contactName as string,
      phoneNumber: data.contactPhone as string,
      npi: (data.npi as string).toString(),
      countryPhoneCode: country === "UK" ? "44" : "1",
      status: data.status,
      createdBy: "66e0600b516d00801590a231", // TODO: get from user DB
    };

    await updateOrganization.mutateAsync(
      {
        id: organization._id,
        data: payload,
      },
      {
        onSuccess: () => {
          onClose();
          showSnackBar(t("messages.update-success"), "success");

          createAuditLog.mutate({
            appType: "WEB_BACK_OFFICE",
            module: MODULE_NAME.ORGANIZATION,
            option: OPTIONS.ORGANIZATION_EDIT,
            actionCode: ACTION_CODE.WEB_OU_EDIT,
            action: "When editing a OU",
            detail: `Edited OU ${data.name}`,
            transactionDate: new Date(),
            accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
            createdBy: "66e0600b516d00801590a231",
            name: userInfo?.firstName + " " + userInfo?.lastName,
          });
        },
        onError: (error: ErrorResponse) => {
          showSnackBar(t("messages.update-error"), "error");
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={false}
      title={t("modals.title-edit")}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 440,
        maxHeight: 720,
        maxWidth: 860,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", minHeight: 380 }}>
            <Box
              sx={{
                padding: "20px 0 0",
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              }}
            >
              <Typography variant="headlineSmall">
                {t("modals.name")}
              </Typography>
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                aria-label="invite user tabs"
              >
                <Tab label={t("tabs.general")} {...a11yProps(0)} />
                <Tab label={t("tabs.address")} {...a11yProps(1)} />
                <Tab label={t("tabs.contact")} {...a11yProps(2)} />
                <Tab
                  label={t("tabs.additional-information")}
                  {...a11yProps(3)}
                />
              </Tabs>
            </Box>
            <TabPanel value={selectedTab} index={0}>
              <TabOrganizationGeneral editMode={true} />
            </TabPanel>
            <TabPanel value={selectedTab} index={1}>
              <TabOrganizationAddress />
            </TabPanel>
            <TabPanel value={selectedTab} index={2}>
              <TabOrganizationContact />
            </TabPanel>
            <TabPanel value={selectedTab} index={3}>
              <TabOrganizationAdditionalInfo />
            </TabPanel>
          </Box>
        </FormProvider>
      }
      actions={
        <Box
          sx={{
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={false}
            label={t("buttons.cancel")}
          />
          <Button
            variantType="save"
            onClick={async () => {
              methods.handleSubmit(onSubmit)();
            }}
            disabled={false}
            isLoading={false}
            label={t("buttons.update")}
          />
        </Box>
      }
    />
  );
};

export default OrganizationUpdateModal;
